import { Extension, mergeAttributes } from '@tiptap/core'
import Document from '@tiptap/extension-document'
import Mention from '@tiptap/extension-mention'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import { Placeholder } from '@tiptap/extensions'
import { paletteFor } from '../../../shared/catalog.js'
import { encodeMention } from '../../../shared/mentions.js'

/**
 * TipTap extensions for the question composer: plain text with optional
 * @system tags, Enter to send, Shift-Enter for a new line.
 *
 * Tags are atomic inline nodes { id: slug, label: name }. Their text form
 * (editor.getText) is the stored token `@[Name](slug)`, so serialising the
 * question is just getText().
 *
 * @param {{ getSystem: (slug) => object|undefined, placeholder: string,
 *           suggestion: object, onSubmit: () => boolean }} options
 */
export function composerExtensions({ getSystem, placeholder, suggestion, onSubmit }) {
  const SubmitOnEnter = Extension.create({
    name: 'submitOnEnter',
    addKeyboardShortcuts() {
      return {
        Enter: () => onSubmit(),
        'Mod-Enter': () => onSubmit(),
        'Shift-Enter': ({ editor }) => editor.commands.splitBlock(),
      }
    },
  })

  return [
    Document,
    Paragraph,
    Text,
    Placeholder.configure({ placeholder }),
    Mention.configure({
      deleteTriggerWithBackspace: true,
      renderText({ node }) {
        return encodeMention({ slug: node.attrs.id, name: node.attrs.label ?? node.attrs.id })
      },
      renderHTML({ node, options }) {
        const system = getSystem(node.attrs.id)
        const palette = paletteFor(system?.palette)
        const label = node.attrs.label ?? system?.name ?? node.attrs.id
        return [
          'span',
          mergeAttributes(options.HTMLAttributes, {
            'data-type': 'mention',
            'data-id': node.attrs.id,
            'data-label': label,
            class: 'kb-mention',
            style: `background:${palette.chipBg};color:${palette.chipText}`,
            contenteditable: 'false',
          }),
          ['span', { class: 'kb-mention-tile', style: `background:${palette.tile}`, 'aria-hidden': 'true' }, system?.letter ?? label[0]],
          `@${label}`,
        ]
      },
      suggestion,
    }),
    SubmitOnEnter,
  ]
}

/** Slugs of the tagged systems in document order (unique). */
export function mentionSlugs(editor) {
  const slugs = []
  editor?.state.doc.descendants((node) => {
    if (node.type.name === 'mention' && !slugs.includes(node.attrs.id)) slugs.push(node.attrs.id)
  })
  return slugs
}

/** The question as stored: text with @[Name](slug) tokens. */
export function serializeQuestion(editor) {
  return editor.getText({ blockSeparator: '\n' }).replace(/\u00a0/g, ' ').trim()
}
